import React, { useState, useEffect, useMemo } from 'react';
import { X, Layers, Package, Info, ArrowRight } from 'lucide-react';
import { produccionService, ProduccionConMetricas } from '../services/produccionService';
import { Produccion, ProductoActividad } from '../lib/supabase';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { DialogShell } from './ui/DialogShell';
import { TextInput } from './ui/TextInput';
import { Select } from './ui/Select';
import { FORM_BORDER, FORM_BORDER_HOVER, FOCUS_RING, FORM_RADIUS } from './ui/tokens';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

function calcAutoInitLote(
  produccionesExistentes: ProduccionConMetricas[],
  productos: ProductoActividad[]
) {
  const siguienteNumero = produccionesExistentes.length + 1;
  const productoUnico = productos.length === 1 ? productos[0] : null;
  const productoIdAuto = productoUnico ? productoUnico.id : '';
  const activarMigracion = !!productoUnico
    && productoUnico.stock_actual > 0
    && !produccionesExistentes.some(p => p.producto_id === productoUnico.id);
  return {
    formData: {
      producto_id: productoIdAuto,
      nombre: `Lote ${siguienteNumero}`,
      stock_inicial: activarMigracion && productoUnico ? productoUnico.stock_actual.toString() : '',
      fecha_inicio: '',
      fecha_fin: '',
    },
    migrarStock: activarMigracion,
  };
}

interface ProduccionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  actividadId: string;
  produccion?: Produccion;
  productos: ProductoActividad[];
  produccionesExistentes?: ProduccionConMetricas[];
}

export const ProduccionModal: React.FC<ProduccionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  actividadId,
  produccion,
  productos,
  produccionesExistentes = []
}) => {
  const [loading, setLoading] = useState(false);
  const { error, clearError, handleError } = useErrorHandler();

  const [formData, setFormData] = useState({
    producto_id: '',
    nombre: '',
    stock_inicial: '',
    fecha_inicio: '',
    fecha_fin: ''
  });
  const [migrarStock, setMigrarStock] = useState(false);

  const isEditing = !!produccion;

  // Producto seleccionado para mostrar info
  const productoSeleccionado = productos.find(p => p.id === formData.producto_id);

  // Verificar si es el primer lote de este producto
  const esPrimerLoteDelProducto = useMemo(() => {
    if (!formData.producto_id || isEditing) return false;
    return !produccionesExistentes.some(p => p.producto_id === formData.producto_id);
  }, [formData.producto_id, produccionesExistentes, isEditing]);

  // Verificar si el producto tiene stock disponible para migrar
  const stockDisponibleParaMigrar = productoSeleccionado?.stock_actual || 0;

  useEffect(() => {
    if (produccion) {
      setFormData({
        producto_id: produccion.producto_id || '',
        nombre: produccion.nombre,
        stock_inicial: produccion.stock_inicial?.toString() || '',
        fecha_inicio: produccion.fecha_inicio ? produccion.fecha_inicio.split('T')[0] : '',
        fecha_fin: produccion.fecha_fin ? produccion.fecha_fin.split('T')[0] : ''
      });
    } else {
      const init = calcAutoInitLote(produccionesExistentes, productos);
      setFormData(init.formData);
      setMigrarStock(init.migrarStock);
    }
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produccion, isOpen, produccionesExistentes.length, productos]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!formData.producto_id) {
      handleError('Debes seleccionar un producto');
      return;
    }

    if (!formData.stock_inicial || parseInt(formData.stock_inicial) <= 0) {
      handleError('El stock inicial debe ser mayor a 0');
      return;
    }

    setLoading(true);

    try {
      if (isEditing && produccion) {
        await produccionService.update(produccion.id, {
          nombre: formData.nombre,
          stock_inicial: parseInt(formData.stock_inicial),
          fecha_inicio: formData.fecha_inicio || null,
          fecha_fin: formData.fecha_fin || null
        });
      } else {
        await produccionService.create({
          actividad_id: actividadId,
          producto_id: formData.producto_id,
          nombre: formData.nombre,
          stock_inicial: parseInt(formData.stock_inicial),
          fecha_inicio: formData.fecha_inicio || undefined,
          fecha_fin: formData.fecha_fin || undefined
        });
      }

      onSuccess();
      onClose();
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos guardar el lote.');
    } finally {
      setLoading(false);
    }
  };

  let submitButtonLabel = 'Guardando...';
  if (!loading) {
    submitButtonLabel = isEditing ? 'Guardar Cambios' : 'Crear Lote';
  }

  if (!isOpen) return null;

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      header={
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Layers className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {isEditing ? 'Editar Lote' : 'Nuevo Lote'}
              </h2>
              <p className="text-purple-100 text-sm">
                {isEditing ? 'Actualiza la información del lote' : 'Crea un nuevo lote para un producto'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      }
    >
        <form onSubmit={handleSubmit} className="space-y-5">
          <FormErrorAlert error={error} onClear={clearError} />

          {/* Producto */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <Package className="w-4 h-4 inline mr-1" />
              Producto {productos.length === 1 && <span className="text-purple-600 text-xs font-normal">(Único producto disponible)</span>}
              <span className="text-red-500">*</span>
            </label>
            {productos.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
                <Info className="w-4 h-4 inline mr-1" />
                Primero debes crear al menos un producto en la pestaña "Productos"
              </div>
            ) : (
              <Select
                options={productos.map((p: ProductoActividad) => ({
                  value: p.id,
                  label: `${p.nombre_producto} ($${p.precio_unitario.toLocaleString()})`,
                }))}
                value={formData.producto_id}
                onChange={(productoId) => {
                  const producto = productos.find(p => p.id === productoId);
                  const tieneStock = producto && producto.stock_actual > 0;
                  const esPrimerLote = !produccionesExistentes.some(p => p.producto_id === productoId);
                  const debeActivarMigracion = !!(tieneStock && esPrimerLote);
                  setFormData({
                    ...formData,
                    producto_id: productoId,
                    stock_inicial: debeActivarMigracion && producto ? producto.stock_actual.toString() : ''
                  });
                  setMigrarStock(debeActivarMigracion);
                }}
                placeholder="Selecciona un producto"
                disabled={isEditing || productos.length === 1}
                required
              />
            )}
            {isEditing && (
              <p className="text-xs text-slate-500 mt-1">
                El producto no se puede cambiar después de crear el lote
              </p>
            )}
          </div>

          {/* Info del producto seleccionado */}
          {productoSeleccionado && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-purple-700">Precio unitario:</span>
                <span className="font-bold text-purple-900">${productoSeleccionado.precio_unitario.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-purple-700">Stock actual del producto:</span>
                <span className="font-bold text-purple-900">{productoSeleccionado.stock_actual} uds</span>
              </div>
            </div>
          )}

          {/* Opción de migrar stock - Solo para primer lote del producto */}
          {esPrimerLoteDelProducto && stockDisponibleParaMigrar > 0 && (
            <div className={`rounded-lg p-4 ${migrarStock ? 'bg-blue-50 border border-blue-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="migrarStock"
                  checked={migrarStock}
                  onChange={(e) => {
                    setMigrarStock(e.target.checked);
                    if (e.target.checked) {
                      setFormData({ ...formData, stock_inicial: stockDisponibleParaMigrar.toString() });
                    } else {
                      setFormData({ ...formData, stock_inicial: '' });
                    }
                  }}
                  className="mt-1 h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                />
                <label htmlFor="migrarStock" className="flex-1 cursor-pointer">
                  {migrarStock ? (
                    <>
                      <span className="font-semibold text-blue-800 flex items-center gap-2">
                        <ArrowRight className="w-4 h-4" />
                        Migrar stock existente a este lote (recomendado)
                      </span>
                      <p className="text-xs text-blue-600 mt-1">
                        El producto tiene {stockDisponibleParaMigrar} unidades. Al activar esta opción, 
                        este lote tomará el control del stock para mejor seguimiento.
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-amber-800 flex items-center gap-2">
                        <Info className="w-4 h-4" />
                        Stock desconectado
                      </span>
                      <p className="text-xs text-amber-700 mt-1">
                        El lote tendrá su propio stock independiente. Las {stockDisponibleParaMigrar} unidades 
                        del producto quedarán sin seguimiento en lotes.
                      </p>
                    </>
                  )}
                </label>
              </div>
            </div>
          )}

          {/* Info: Ya existen lotes para este producto */}
          {!esPrimerLoteDelProducto && formData.producto_id && !isEditing && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <Info className="w-4 h-4 inline mr-1" />
                Este producto ya tiene lotes. El stock de este nuevo lote será independiente.
              </p>
            </div>
          )}

          <TextInput
            label="Stock Inicial del Lote"
            type="number"
            value={formData.stock_inicial}
            onChange={(v) => setFormData({ ...formData, stock_inicial: v })}
            min={1}
            placeholder="Cantidad de unidades a producir"
            required
          />

          <TextInput
            label="Nombre del Lote"
            value={formData.nombre}
            onChange={(v) => setFormData({ ...formData, nombre: v })}
            placeholder="Ej: Lote 1, Lote Navidad, etc."
            required
          />

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="produccion-fecha-inicio" className="block text-sm font-medium text-slate-600 mb-1.5">
                Fecha de Inicio
              </label>
              <input
                id="produccion-fecha-inicio"
                type="date"
                value={formData.fecha_inicio}
                onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
              />
            </div>
            <div>
              <label htmlFor="produccion-fecha-fin" className="block text-sm font-medium text-slate-600 mb-1.5">
                Fecha de Fin
              </label>
              <input
                id="produccion-fecha-fin"
                type="date"
                value={formData.fecha_fin}
                onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                min={formData.fecha_inicio || undefined}
                className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
              />
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || productos.length === 0}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors disabled:opacity-50"
            >
              {submitButtonLabel}
            </button>
          </div>
        </form>
    </DialogShell>
  );
};
